/**
 * /templates/new  — author a NEW reusable template from a blank canvas
 * /templates/:templateId/edit — re-edit an existing template's design/name
 *
 * round8/template-authoring. Replaces the old "Save as template" card that
 * lived inside LeadDetailPage: template authoring is no longer a side effect
 * of looking at one homeowner's lead, it is its own destination reached from
 * Settings → Templates. Creation from a lead is GONE, deliberately — see
 * SettingsPage's Templates section for the entry points.
 *
 * WHY A ROUTE AND NOT A MODAL INSIDE THE SETTINGS SECTION: this mounts the
 * real dock designer in an iframe. That needs the whole viewport to be
 * usable, wants to be linkable/bookmarkable, and keeps the a11y story
 * tractable (its own single <h1>, its own <main> landmark, and
 * RouteFocusManager's existing route-change focus handling applies for free).
 * A 600px-tall designer stuffed into a settings card would be neither.
 *
 * ── The embed contract this page drives (lib/embedMessaging.ts) ──
 *
 * The iframe is mounted with `{ edit: true, author: true }` — i.e.
 * `?builder=<tenantId>&preview=1&edit=1&author=1`. The `author=1` part is
 * NOT cosmetic and must never be dropped: on the plain `edit=1` surface the
 * embed renders its homeowner "Request Quote" CTA, wired to a real
 * POST /api/submit. Without `author=1` a builder drawing a template could
 * file a genuine lead into their own tenant and burn a month of submit
 * quota. `author=1` suppresses that CTA and its modal, plus the homeowner
 * first-run onboarding and the autosave restore banner.
 *
 * SAVE READINESS DIFFERS BY MODE, and this is the subtle part:
 *   - EDIT mode pushes the existing design in via `load-design` and must
 *     wait for `design-loaded` before allowing a save. Saving earlier would
 *     snapshot the embed's own 12x24 starter dock and overwrite the real
 *     template with it.
 *   - NEW mode pushes NOTHING (the starter dock IS the intended blank
 *     canvas) and therefore can never receive `design-loaded` at all — the
 *     embed only emits it in response to a `load-design`. So new-template
 *     saves gate on `embed-ready` alone. Gating them on `design-loaded`
 *     would wait forever.
 * Getting this backwards is silent in one direction (a save button that
 * never enables) and destructive in the other (clobbering a template with
 * the starter dock), which is why both flags are tracked separately below.
 *
 * A snapshot payload of `null` means the canvas is empty — reachable via the
 * designer's own Clear button, and serializePreset returns null for an empty
 * grid. That is "nothing to save", NOT a valid template, and is rejected
 * before any write.
 *
 * Owner-only: every template write is owner-only server-side. A `rep` is
 * shown a read-only explanation rather than a form that would 403, matching
 * how SettingsPage gates its own owner-only sections.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../contexts/auth-context";
import { isTenantUser } from "../lib/authRole";
import {
  ApiError, listTemplates, createTemplate, updateTemplate,
  type TenantTemplate,
} from "../lib/api";
import {
  buildEmbedUrl, getEmbedOrigin, isEmbedReady, isDesignLoaded, isDesignSnapshot,
} from "../lib/embedMessaging";
import { StatusMessage } from "../components/StatusMessage";
import { friendlyErrorMessage } from "../components/friendlyErrorMessage";
import { templateWriteErrorMessage } from "../lib/templateErrors";
import ui from "../styles/ui.module.css";
import styles from "./TemplateEditorPage.module.css";

// How long to wait for a design-snapshot reply before giving up. Same
// generous budget LeadDetailPage uses for the identical round trip — this is
// same-page postMessage, not a network call, but the embed may be mid-render.
const SNAPSHOT_TIMEOUT_MS = 10_000;

const MAX_TEMPLATE_NAME = 100;

export function TemplateEditorPage() {
  const { templateId } = useParams<{ templateId: string }>();
  const isNew = !templateId;
  const { user, signOut } = useAuth();
  const navigate = useNavigate();

  const iframeRef = useRef<HTMLIFrameElement>(null);

  const [name, setName] = useState("");
  const [embedReady, setEmbedReady] = useState(false);
  const [designPushed, setDesignPushed] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedMessage, setSavedMessage] = useState<string | null>(null);

  // Edit mode only: the template being edited. There is no single-template
  // GET endpoint (the API exposes list/create/update/delete), so this is
  // resolved by filtering the tenant's list — the same source the Settings
  // Templates section reads.
  const [existing, setExisting] = useState<TenantTemplate | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(!isNew);

  // The one in-flight request-snapshot's requestId + settlers, or null when
  // none is outstanding. A single slot is sufficient: the Save button
  // disables itself while saving, so at most one round trip is ever live.
  const pendingSnapshotRef = useRef<{
    requestId: string;
    resolve: (payload: unknown) => void;
    reject: (err: Error) => void;
  } | null>(null);

  const isOwner = user?.role === "owner";

  // `tenantId` lives only on the TENANT arm of the AuthenticatedUser union —
  // an orgAdmin genuinely has no tenant (see lib/auth.ts's own note, which
  // calls out LeadDetailPage as the page that narrows this way to build an
  // embed URL; this page is the second such consumer). ProtectedRoute keeps
  // an orgAdmin off tenant routes, so the null arm is a guard, not a UX path.
  const tenantUser = user && isTenantUser(user) ? user : null;

  const embedUrl = useMemo(
    () => (tenantUser ? buildEmbedUrl(tenantUser.tenantId, { edit: true, author: true }) : ""),
    [tenantUser],
  );

  // Edit mode: resolve the template by id out of the tenant's list.
  useEffect(() => {
    if (isNew || !user) return;
    let cancelled = false;
    // No setIsLoading(true) here: it is initialized to !isNew, so edit mode
    // already starts loading, and setting it synchronously in the effect body
    // is both redundant and a react-hooks/set-state-in-effect violation.
    listTemplates(user.idToken)
      .then((templates) => {
        if (cancelled) return;
        const found = templates.find((t) => t.id === templateId);
        if (!found) {
          setLoadError("That template no longer exists — it may have been deleted.");
          return;
        }
        setExisting(found);
        setName(found.name);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 401) {
          signOut();
          return;
        }
        setLoadError(friendlyErrorMessage(err, "Failed to load the template."));
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => { cancelled = true; };
  }, [isNew, templateId, user, signOut]);

  // Shared message listener. embed-ready / design-loaded flip flags;
  // design-snapshot resolves whichever request-snapshot is awaiting one
  // (matched by requestId — belt-and-suspenders against a stray reply,
  // since only one is ever outstanding).
  useEffect(() => {
    const allowedOrigin = getEmbedOrigin();
    const handler = (event: MessageEvent) => {
      if (allowedOrigin && event.origin !== allowedOrigin) return;
      if (isEmbedReady(event.data)) {
        setEmbedReady(true);
        return;
      }
      if (isDesignLoaded(event.data)) {
        setDesignPushed(true);
        return;
      }
      if (isDesignSnapshot(event.data)) {
        const pending = pendingSnapshotRef.current;
        if (pending && event.data.requestId === pending.requestId) {
          pendingSnapshotRef.current = null;
          pending.resolve(event.data.payload);
        }
        return;
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, []);

  // Edit mode: push the existing design in once the embed announces
  // readiness. New mode deliberately pushes nothing — the embed's own
  // starter dock is the blank canvas (see this file's header).
  useEffect(() => {
    if (isNew || !embedReady || !existing) return;
    const allowedOrigin = getEmbedOrigin();
    iframeRef.current?.contentWindow?.postMessage(
      { type: "load-design", payload: existing.designState },
      allowedOrigin || "*",
    );
  }, [isNew, embedReady, existing]);

  // Save is allowed once the canvas genuinely reflects what the builder
  // expects: embed-ready alone for a blank canvas, but design-loaded for an
  // edit (where saving early would snapshot the starter dock over the real
  // template). See the header's readiness note.
  const canSave = isNew ? embedReady : embedReady && designPushed;

  const requestSnapshotFromEmbed = useCallback((): Promise<unknown> => {
    return new Promise((resolve, reject) => {
      const win = iframeRef.current?.contentWindow;
      if (!win) {
        reject(new Error("The design editor isn't available right now."));
        return;
      }
      const requestId = typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `snap-${Date.now()}-${Math.random().toString(36).slice(2)}`;

      const timeoutId = window.setTimeout(() => {
        if (pendingSnapshotRef.current?.requestId === requestId) {
          pendingSnapshotRef.current = null;
          reject(new Error("Timed out waiting for the design editor to respond."));
        }
      }, SNAPSHOT_TIMEOUT_MS);

      pendingSnapshotRef.current = {
        requestId,
        resolve: (payload) => { window.clearTimeout(timeoutId); resolve(payload); },
        reject: (err) => { window.clearTimeout(timeoutId); reject(err); },
      };

      const allowedOrigin = getEmbedOrigin();
      win.postMessage({ type: "request-snapshot", requestId }, allowedOrigin || "*");
    });
  }, []);

  const handleSave = useCallback(async (e: FormEvent) => {
    e.preventDefault();
    if (!user || isSaving) return;

    const trimmed = name.trim();
    if (!trimmed) {
      setSaveError("Enter a name for the template.");
      return;
    }

    setIsSaving(true);
    setSaveError(null);
    setSavedMessage(null);
    try {
      const snapshot = await requestSnapshotFromEmbed();
      // An empty canvas serializes to null (see the header) — never write it.
      if (!snapshot) {
        setSaveError("The canvas is empty — draw a dock before saving this template.");
        return;
      }

      if (isNew) {
        const created = await createTemplate(user.idToken, { name: trimmed, designState: snapshot });
        // Navigate back rather than staying: this template now exists, and a
        // second save from this same page would create a DUPLICATE rather
        // than update the one just written.
        navigate("/templates", {
          replace: true,
          state: { templateSaved: created.name },
        });
        return;
      }

      const saved = await updateTemplate(user.idToken, templateId!, {
        name: trimmed,
        designState: snapshot,
      });
      // Trust the server's sanitized copy over the local snapshot.
      setExisting(saved);
      setName(saved.name);
      setSavedMessage(`Saved “${saved.name}”.`);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        signOut();
        return;
      }
      setSaveError(templateWriteErrorMessage(err));
    } finally {
      setIsSaving(false);
    }
  }, [user, isSaving, name, requestSnapshotFromEmbed, isNew, navigate, templateId, signOut]);

  if (!user || !tenantUser) return null;

  const heading = isNew ? "New template" : (existing ? `Edit ${existing.name}` : "Edit template");

  return (
    <main id="main-content" className={`${ui.main} ${styles.main}`}>
      <Link to="/templates" className={ui.backLink}>&larr; Back to templates</Link>
      <h1>{heading}</h1>

      {!isOwner ? (
        <section className={ui.card}>
          <p className={ui.subtle}>
            Only the account owner can create or edit templates.
          </p>
        </section>
      ) : loadError ? (
        <section className={ui.card}>
          <StatusMessage variant="error" message={loadError} className={ui.error} />
          <p className={`${ui.subtle} ${styles.afterError}`}>
            <Link to="/templates">Back to templates</Link>
          </p>
        </section>
      ) : (
        <form onSubmit={handleSave} className={styles.editorForm}>
          <section className={`${ui.card} ${styles.nameCard}`}>
            <label className={`${ui.field} ${styles.nameField}`}>
              <span>Template name</span>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={"e.g. \"Standard 12x30 pier dock\""}
                maxLength={MAX_TEMPLATE_NAME}
                disabled={isLoading}
              />
            </label>
            <p className={`${ui.subtle} ${styles.nameHint}`}>
              {isNew
                ? "Draw the dock you want homeowners to be able to start from. It opens on a blank 12 × 24 ft pier dock — resize it, or clear it and sketch your own."
                : "Changes here affect what new homeowners start from. Designs homeowners already created from this template are their own copies and won't change."}
            </p>
          </section>

          <div className={styles.iframeContainer}>
            {!embedReady && (
              <p className={styles.iframeLoading}>Loading design editor…</p>
            )}
            {!isNew && embedReady && !designPushed && (
              <p className={styles.iframeLoading}>Applying this template&rsquo;s design…</p>
            )}
            <iframe
              ref={iframeRef}
              src={embedUrl}
              sandbox="allow-scripts allow-same-origin"
              /* round12/portal-embed-fullscreen: the embed's own full-screen
                 toggle (round11) renders only when the host grants the
                 permission — without these two the browser reports
                 full-screen as unavailable inside the frame and the button
                 is simply absent. `allow` is the Permissions-Policy form,
                 `allowFullScreen` the legacy attribute older engines read. */
              allow="fullscreen"
              allowFullScreen
              className={styles.iframe}
              title={isNew ? "Dock template editor" : "Dock template editor"}
            />
          </div>

          <div className={ui.saveBar}>
            <button
              type="submit"
              className={ui.button}
              disabled={isSaving || !canSave || isLoading}
            >
              {isSaving ? "Saving…" : (isNew ? "Save template" : "Save changes")}
            </button>
            <Link to="/templates" className={ui.linkButton}>Cancel</Link>
            {savedMessage && (
              <StatusMessage variant="status" message={savedMessage} className={ui.savedInline} />
            )}
            {saveError && (
              <StatusMessage variant="error" message={saveError} className={ui.error} />
            )}
            {!canSave && !isSaving && !savedMessage && !saveError && (
              <span className={`${ui.subtle} ${styles.saveHint}`}>Save enables once the designer is ready.</span>
            )}
          </div>
        </form>
      )}
    </main>
  );
}
